'use client';

import { AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

import { formatTimeLeft } from '@kit/shared/utils';
import { Button } from '@kit/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@kit/ui/dropdown-menu';
import { If } from '@kit/ui/if';
import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

import {
  FileUploaderProvider,
  useFileUploader,
} from '../hooks/use-file-uploader';
import { getStateColor, uploadKeyMap } from '../lib/utils';
import FileUploader from './file-uploader';

export function UploadManager({ children }: { children: React.ReactNode }) {
  return <FileUploaderProvider>{children}</FileUploaderProvider>;
}

/**
 * The UI component for the upload manager that displays a floating button
 * and dropdown with upload progress information.
 */
export function UploadManagerUI() {
  const { files, isOpen, setIsOpen } = useFileUploader();

  const uploadingFiles = files.filter((f) => f.status === 'uploading');

  if (files.filter((f) => f.status !== 'pending').length === 0) {
    return null;
  }

  const overallProgress =
    files.reduce((acc, file) => acc + file.progress, 0) / files.length || 0;

  const uploadState = files.some((f) => f.status === 'paused')
    ? 'paused'
    : files.some((f) => f.status === 'error')
      ? 'error'
      : files.some((f) => f.status === 'uploading')
        ? 'uploading'
        : 'completed';

  const hasErrors = files.some((f) => f.status === 'error');

  const totalSpeed = uploadingFiles.reduce(
    (acc, file) => acc + (file.uploadSpeed || 0),
    0,
  );

  const totalETA = uploadingFiles.reduce(
    (acc, file) => acc + (file.eta || 0),
    0,
  );

  const formattedSpeed =
    totalSpeed > 0 ? `${(totalSpeed / (1024 * 1024)).toFixed(1)} MB/s` : '';

  const filesCountByKey = files
    .filter((f) => f.status === 'paused')
    .reduce((acc, file) => {
      if (file.uploadKey) {
        const uploadKey = uploadKeyMap(file.uploadKey);
        acc.set(uploadKey, (acc.get(uploadKey) || 0) + 1);
      }
      return acc;
    }, new Map<string, number>());

  return (
    <div className="">
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5">
              {uploadingFiles.length > 0 && (
                <span className="text-muted-foreground bg-muted/50 rounded-full px-2 py-0.5 text-xs font-medium">
                  {formattedSpeed}
                </span>
              )}
              {uploadingFiles.length > 0 && totalSpeed > 0 && (
                <span className="text-muted-foreground bg-muted/50 rounded-full px-2 py-0.5 text-xs font-medium">
                  {formatTimeLeft(totalETA)}
                </span>
              )}
            </div>
            <Button
              className={cn(
                'group relative h-8 w-8 rounded-full shadow-lg transition-all duration-300',
                getStateColor(uploadState),
                'hover:scale-105 hover:shadow-xl',
                'before:absolute before:inset-0 before:rounded-full before:bg-white/10',
              )}
              onClick={() => setIsOpen(!isOpen)}
            >
              {/* Circular progress indicator */}
              <svg
                className="absolute inset-0"
                viewBox="0 0 100 100"
                style={{
                  transform: 'rotate(-90deg)',
                }}
              >
                <circle
                  className="stroke-white/20"
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  strokeWidth="4"
                />
                <circle
                  className={cn(
                    'transition-all duration-300',
                    hasErrors ? 'stroke-white/60' : 'stroke-white',
                  )}
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  strokeWidth="4"
                  strokeDasharray={`${overallProgress * 2.827433388230814}, 282.7433388230814`}
                  strokeLinecap="round"
                />
              </svg>

              {/* Upload stats display */}
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                <span
                  className={cn(
                    'font-bold',
                    uploadingFiles.length > 0 && 'animate-pulse',
                  )}
                >
                  {uploadingFiles.length}
                </span>
              </div>
            </Button>
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="max-h-[600px] min-h-[100px] min-w-[400px] overflow-y-auto p-0"
        >
          {/* Upload panel header */}
          <div className="bg-background flex items-center justify-between p-2">
            <h3 className="font-medium">
              <Trans i18nKey="views:uploadManager.uploads" />
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsOpen(false)}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="p-4 pt-0">
            <AnimatePresence>
              <FileUploader />
            </AnimatePresence>
          </div>

          <If condition={uploadState === 'paused'}>
            <div className="bg-background flex flex-col gap-2 p-2">
              <span className="text-sm text-amber-500">
                <Trans
                  i18nKey="views:uploadManager.pausedDescription"
                  values={{
                    uploadKey: Array.from(filesCountByKey.entries())
                      .map(([key, value]) => `${key} (${value})`)
                      .join(', '),
                  }}
                />
              </span>
              <span className="text-sm text-amber-500">
                <Trans i18nKey="views:uploadManager.pausedDescription2" />
              </span>
            </div>
          </If>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
